import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { fetchAuthSession } from "@aws-amplify/auth";
import { configureAuth } from "../../auth/authConfig";
import { isAdminSession } from "../../auth/adminAuthConfig";

import { withSessionLock } from "../../auth/sessionQueue";

export default function ProtectedAdminRoute({ children }) {
  const [access, setAccess] = useState(null);
  const location = useLocation();

  useEffect(() => {
    let alive = true, checking = false, timer;
    const check = async () => {
      if (checking || !alive) return;
      checking = true;
      clearTimeout(timer);
      try {
        const session = await withSessionLock(async () => {
          configureAuth("admin");
          return fetchAuthSession();
        });
        if (!alive) return;
        const allowed = isAdminSession(session);
        setAccess({ key: location.key, allowed, sub: session?.tokens?.idToken?.payload?.sub });
        if (allowed) {
          const remaining = session.tokens.idToken.payload.exp * 1000 - Date.now();
          timer = setTimeout(() => { setAccess(null); check(); }, Math.min(2147483647, Math.max(0, remaining)));
        }
      } catch {
        if (alive) setAccess({ key: location.key, allowed: false });
      } finally {
        checking = false;
      }
    };
    const visible = () => { if (document.visibilityState === "visible") check(); };
    check();
    window.addEventListener("focus", check);
    window.addEventListener("storage", check);
    document.addEventListener("visibilitychange", visible);
    return () => {
      alive = false;
      clearTimeout(timer);
      window.removeEventListener("focus", check);
      window.removeEventListener("storage", check);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [location.key]);

  if (!access || access.key !== location.key) return <p role="status" className="p-8 text-center text-[#3F256B]">Checking admin access...</p>;
  if (!access.allowed) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/admin/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <React.Fragment key={access.sub}>{children}</React.Fragment>;
}
